import { computed } from "vue";
import { useDayReportsStoreRefactor } from "@/stores/dayReportsStoreRefactor";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";
import { useDayReportLinesStoreRefactor } from "@/stores/dayReportLinesStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";

/**
 * Composable for refreshing day report and all related data
 * @param {Ref|ComputedRef} dayReportRef - Reactive reference to the day report object
 * @returns {Object} - Object containing refresh function and loading state
 */
export function useDayReportRefresh(dayReportRef) {
  // Store instances
  const dayReportsStore = useDayReportsStoreRefactor();
  const accountsStore = useAccountsStoreRefactor();
  const reportsStore = useReportsStoreRefactor();
  const dayReportLinesStore = useDayReportLinesStoreRefactor();
  const alertStore = useAlertStore();

  // Computed day report ID for safety
  const dayReportId = computed(() => dayReportRef.value?.id);

  /**
   * Refresh day report and all related data
   */
  async function refreshDayReport() {
    console.log("useDayReportRefresh: refreshDayReport function called!");
    alertStore.info("Dagrapport wordt vernieuwd...");

    try {
      console.log(
        `useDayReportRefresh: Refreshing day report ${dayReportId.value} and all related data`,
      );

      if (!dayReportId.value) {
        throw new Error("No day report ID available for refresh");
      }

      // Refresh the day report itself
      await dayReportsStore.fetchById(dayReportId.value);

      // Refresh reports for this day report
      await reportsStore.getByDayReport(dayReportId.value, {
        forceRefresh: true,
      });

      // Refresh day report lines
      await dayReportLinesStore.getForDayReport(dayReportId.value, true);

      // Get all reports for this day report to find account IDs
      const dayReportReports = reportsStore.getReportsByDayReport(
        dayReportId.value,
      );

      // Get unique account IDs and refresh each account
      const accountIds = [
        ...new Set(
          dayReportReports.map((report) => report.account_id).filter(Boolean),
        ),
      ];
      console.log(
        `useDayReportRefresh: Refreshing ${accountIds.length} accounts`,
      );

      await Promise.all(
        accountIds.map((accountId) => accountsStore.fetchById(accountId)),
      );

      console.log(
        "useDayReportRefresh: Day report and related data refreshed successfully",
      );
      alertStore.success("Dagrapport succesvol vernieuwd");
    } catch (error) {
      console.error("useDayReportRefresh: Error refreshing day report:", error);
      alertStore.error(
        "Er is een probleem opgetreden bij het vernieuwen van het dagrapport",
      );
    }
  }

  return {
    refreshDayReport,
    dayReportId,
  };
}
